<template>
  <div class="page">
    <h1 class="h1--underline">
      {{ $tv('guideDetailTitle', 'guide') + ' | ' + guideTitle }}
    </h1>
    <div
      v-for="step in guide?.Steps"
      :key="step?.id"
    >
      <Step :step="step" />
    </div>
    <NuxtLink
      :to="guidePath"
      class="k-button k-button-md k-button-solid k-button-solid-base"
    >
      <NavigationChevronLeft class="svg-navigation-chevron-left" />
      <span class="page__button--content">{{ $tv('go-back-button', 'guide') }}</span>
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import {useGuideService} from '~/composables/guide_service'
import Step from '~/components/guide/Step.vue'
import NavigationChevronLeft from '~/assets/icons/navigation_chevron_left.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'
import useBreadcrumb from '~/composables/useBreadcrumb'

const i18n = useI18nExtended()
const {getGuideByUrl, getGuideById} = useGuideService()
const route = useRoute()
const nuxtApp = useNuxtApp()
const params = route?.params

const guide = ref(null)
const guidePath = nuxtApp.vueApp.localePath('/guides')

const setupTranslatedParams = async (i18n, guideData) => {
  if (!guideData?.localizations || guideData.localizations.length === 0) {return}

  let translatedParams = {}

  // Fetch each localization by ID to get the Url
  const localizationPromises = guideData.localizations.map(async (loc) => {
    if (loc.locale !== i18n.locale.value) {
      const localizedData = await getGuideById(loc.id, loc.locale)
      if (localizedData?.Url) {
        translatedParams[loc.locale] = {guideDetail: localizedData.Url}
        if (localizedData?.guide_categories?.length > 0) {
          translatedParams[loc.locale] = {
            ...translatedParams[loc.locale],
            guideCategories: localizedData.guide_categories[0].Url
          }
        }
      }
    }
  })

  await Promise.all(localizationPromises)

  if (Object.entries(translatedParams)?.length > 0) {
    route.meta.nuxtI18n = translatedParams
  }
}

// Initial data fetch
let guideData = nuxtApp.payload?.data[`guides_${i18n.locale.value}_{"field":"Url","operator":"contains","value":"${params.guideDetail}"}_true`]
if (guideData && params.guideDetail) {
  guideData = guideData?.find(n => n.Url.toLowerCase() === params.guideDetail.toLowerCase())
}
if (!guideData) {
  guideData = await getGuideByUrl(params.guideDetail, i18n.locale, true)
}

if (guideData) {
  guide.value = guideData
  await setupTranslatedParams(i18n, guideData)
} else {
  throw showError({statusCode: 404, message: 'Page not found for Guide: ' + params.guideDetail + ', route: ' + route.fullPath})
}

// Watch for locale changes
watch(() => i18n.locale.value, async (newLocale, oldLocale) => {
  if (!newLocale || !oldLocale) {return}
  // Find the localization for the new locale
  const localizedGuide = guide.value?.localizations?.find(l => l.locale === newLocale)

  if (!localizedGuide?.id) {
    // eslint-disable-next-line no-console
    console.warn(`No localization found for locale "${newLocale}"`)
    return
  }

  // Fetch the guide by ID for the new locale
  const newGuideData = await getGuideById(localizedGuide.id, newLocale)
  if (newGuideData) {
    guide.value = newGuideData
    await setupTranslatedParams(i18n, newGuideData)
  }
})

// Computed properties
const guideTitle = computed(() => guide.value?.Title ?? '')

const breadcrumbs = computed(() => [
  {
    label: i18n.te('breadcrumbs.guides') ? i18n.tv('guides', 'breadcrumbs') : 'GuideDetail',
    path: '/guides'
  },
  {
    label: i18n.tv(guide.value?.guide_categories[0]?.Title, 'breadcrumbs'),
    path: `/guides/${encodeURIComponent(guide.value?.guide_categories[0]?.Url)}`
  },
  {
    label: i18n.tv(guide.value?.Title, 'breadcrumbs'),
    path: `/guides/${encodeURIComponent(guide.value?.guide_categories[0]?.Url)}/${encodeURIComponent(guide.value?.Url)}`
  }
])

// Update breadcrumbs initially and when guide changes
useBreadcrumb(breadcrumbs.value)
watch(breadcrumbs, (newBreadcrumbs) => {
  useBreadcrumb(newBreadcrumbs)
})

useHead({
  meta: [{hid: 'robots', name: 'robots', content: 'noindex'}]
})
</script>

<style lang="scss" scoped>
.k-button {
  margin-top: $default-margin-padding-2x;
  margin-bottom: $default-margin-padding-width;

  .svg-navigation-chevron-left {
    font-size: 1.5rem;
    margin-left: -.3em;
    margin-right: .5em;
  }
}
</style>
